import React, { useCallback, useLayoutEffect, useRef } from "react";
import { BaseCellProps } from "@components/spreadsheet/cells";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import styles from "@components/spreadsheet/cells/DropdownCell.module.scss";
import comboboxStyles from "./CatalogItemCombobox.module.scss";
import {
    CatalogItemCombobox,
    CatalogItemSelection,
} from "./CatalogItemCombobox";

export type CatalogItemSelectOrder = "commit-cell-first" | "row-update-first";

interface Props extends BaseCellProps {
    rowId: string;
    entityId?: number;
    currency?: string;
    selectOrder: CatalogItemSelectOrder;
    onSelectCatalogItem: (
        rowId: string,
        selection: CatalogItemSelection,
    ) => void;
    linePrefill?: {
        productOrService: string;
        description: string;
        unitAmount: number | null;
        classIds: string[];
        accrualAccountId: string | null;
        receivablesAccountId: string | null;
    };
}

export const CatalogItemSearchCellShell: React.FC<Props> = ({
    value,
    isEditing,
    finishEditing,
    enterEditModeKey,
    rowId,
    entityId,
    currency,
    selectOrder,
    onSelectCatalogItem,
    linePrefill,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const label = typeof value === "string" ? value : "";

    // The grid's own inputRef is intentionally not forwarded: the grid focuses
    // it synchronously inside the initiating keydown, so the browser would
    // natively insert the typed character on top of the initialSearch seed
    // below, doubling it ("L" -> "LL"). Focusing on the next frame (same
    // pattern as MathCell) lets the native insertion land on the non-editable
    // grid container instead.
    useLayoutEffect(() => {
        if (!isEditing) {
            return;
        }
        const rafId = requestAnimationFrame(() => {
            const input = searchInputRef.current;
            if (input) {
                input.focus();
                input.setSelectionRange(input.value.length, input.value.length);
            }
        });
        return () => cancelAnimationFrame(rafId);
    }, [isEditing]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            // Keep grid navigation out of the picker: typing filters, arrows
            // move the dropdown highlight, and the create-modal flow (portaled
            // footer button) must not close the editor.
            e.stopPropagation();
            const isEventFromWithinCell =
                e.target instanceof Node &&
                containerRef.current?.contains(e.target);
            if (!isEventFromWithinCell) {
                return;
            }
            if (["Enter", "Escape"].includes(e.key)) {
                finishEditing();
            }
        },
        [finishEditing],
    );

    if (!isEditing) {
        return (
            <div className={styles.dropdownCell}>
                <div className={styles.dropdownDisplay}>
                    {label ? (
                        <BreakableText
                            text={label}
                            showTooltip
                            className={styles.dropdownLabel}
                        />
                    ) : (
                        <span className={comboboxStyles.displayPlaceholder}>
                            Find or add item
                        </span>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className={comboboxStyles.cellEditor}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={handleKeyDown}
        >
            <CatalogItemCombobox
                valueLabel={label}
                initialSearch={
                    enterEditModeKey?.length === 1
                        ? enterEditModeKey
                        : undefined
                }
                entityId={entityId}
                currency={currency}
                linePrefill={linePrefill}
                initialOpen
                inputRef={searchInputRef}
                onOpenChange={(open) => {
                    if (!open) {
                        finishEditing();
                    }
                }}
                onCommitFreeText={(text) => {
                    finishEditing(text);
                }}
                onSelect={(selection) => {
                    if (selectOrder === "commit-cell-first") {
                        // finishEditing snapshots stale grid data; row update must follow.
                        finishEditing(selection.productOrService);
                        if (rowId) {
                            onSelectCatalogItem(rowId, selection);
                        }
                        return;
                    }

                    if (rowId) {
                        onSelectCatalogItem(rowId, selection);
                    }
                    finishEditing(selection.productOrService);
                }}
            />
        </div>
    );
};
